import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle, PencilSimple, ArrowClockwise, ShieldCheck } from "@phosphor-icons/react";
import PublicFooter from "@/components/PublicFooter";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_francophone-ops/artifacts/1lh4l6xi_3%20%281%29.PNG";
const publicApi = axios.create({ baseURL: API });

export default function SignInterpreterAgreement() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [typedName, setTypedName] = useState("");
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(null);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  useEffect(() => {
    publicApi
      .get(`/public/sign/interpreter/${token}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: null });
        if (r.data.already_signed) setSigned({ signed_at: r.data.signed_at, contract_number: r.data.contract_number });
      })
      .catch((e) =>
        setState({ loading: false, data: null, error: e?.response?.data?.detail || "Invalid or expired signing link" })
      );
  }, [token]);

  // Signature canvas setup
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Set high-DPI backing
    const rect = c.getBoundingClientRect();
    c.width = rect.width * 2;
    c.height = rect.height * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";

    const pos = (e) => {
      const r = c.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => {
      e.preventDefault();
      drawingRef.current = true;
      emptyRef.current = false;
      const { x, y } = pos(e);
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e) => {
      if (!drawingRef.current) return;
      e.preventDefault();
      const { x, y } = pos(e);
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const end = () => {
      drawingRef.current = false;
    };

    c.addEventListener("mousedown", start);
    c.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    c.addEventListener("touchstart", start, { passive: false });
    c.addEventListener("touchmove", move, { passive: false });
    c.addEventListener("touchend", end);

    return () => {
      c.removeEventListener("mousedown", start);
      c.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      c.removeEventListener("touchstart", start);
      c.removeEventListener("touchmove", move);
      c.removeEventListener("touchend", end);
    };
  }, [state.data]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    emptyRef.current = true;
  };

  const submit = async () => {
    if (!agree) return toast.error("You must agree to the terms");
    if (typedName.trim().length < 3) return toast.error("Please type your full legal name");
    if (emptyRef.current) return toast.error("Please draw your signature");
    setSubmitting(true);
    try {
      const png = canvasRef.current.toDataURL("image/png");
      const r = await publicApi.post(`/public/sign/interpreter/${token}`, {
        typed_name: typedName.trim(),
        signature_png_b64: png,
        agree: true,
      });
      setSigned(r.data);
      toast.success("Agreement signed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Signing failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return <CenterCard><div className="text-slate-400 text-sm">Loading agreement…</div></CenterCard>;
  }
  if (state.error) {
    return (
      <CenterCard>
        <div data-testid="sign-error" className="text-center py-6">
          <div className="text-lg font-semibold text-slate-900 mb-2">Link not valid</div>
          <div className="text-sm text-slate-500">{state.error}</div>
          <div className="text-xs text-slate-400 mt-4">Please contact Unity Language Group operations.</div>
        </div>
      </CenterCard>
    );
  }

  const d = state.data;

  if (signed) {
    return (
      <CenterCard>
        <div data-testid="sign-success" className="text-center py-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50 text-emerald-600 mb-4">
            <CheckCircle size={32} weight="fill" />
          </div>
          <div className="text-xl font-bold text-slate-900 mb-1">Agreement signed</div>
          <div className="text-sm text-slate-500 mb-4">
            Thank you, {d.interpreter.full_name}. A signed copy has been emailed to {d.interpreter.email}.
          </div>
          <div className="inline-block bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-left text-xs text-slate-600 font-mono">
            <div><span className="text-slate-400">Contract:</span> {signed.contract_number || d.contract_number}</div>
            <div><span className="text-slate-400">Signed at:</span> {signed.signed_at || d.signed_at}</div>
          </div>
        </div>
      </CenterCard>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="sign-interpreter-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="Unity Language Group" className="h-10 w-auto" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 font-bold">{d.company.name}</div>
            <div className="text-sm text-slate-500">Independent Contractor Agreement</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {/* Contract preview */}
          <div className="p-6 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-1">Contract</div>
            <div className="font-mono text-sm text-slate-900">{d.contract_number}</div>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <PreviewSection title="Contractor">
              <Row label="Legal name" value={d.interpreter.full_name} />
              <Row label="Email" value={d.interpreter.email} />
              <Row label="Phone" value={d.interpreter.phone} />
              <Row label="Address" value={[d.interpreter.address, d.interpreter.city_state_zip].filter(Boolean).join(", ")} />
              <Row label="Languages" value={(d.interpreter.language_pairs || []).join(", ")} />
              <Row label="Specialties" value={(d.interpreter.specialties || []).join(", ")} />
            </PreviewSection>
            <PreviewSection title="Compensation">
              <Row label="In-person" value={`$${Number(d.interpreter.hourly_rate || 0).toFixed(2)}/hr`} />
              <Row label="OPI (phone)" value={`$${Number(d.interpreter.opi_rate || 0).toFixed(2)}/hr`} />
              <Row label="VRI (video)" value={`$${Number(d.interpreter.vri_rate || 0).toFixed(2)}/hr`} />
              <Row label="Translation" value={`$${Number(d.interpreter.translation_rate || 0).toFixed(4)}/word`} />
            </PreviewSection>
          </div>

          <div className="p-6 bg-slate-50 border-t border-slate-100 text-xs text-slate-700 leading-relaxed" data-testid="full-agreement">
            <div className="font-semibold text-slate-900 mb-3 text-sm">Full Independent Contractor Agreement</div>
            <div className="bg-white border border-slate-200 rounded-md p-4 max-h-96 overflow-y-auto space-y-4">
              {(d.full_agreement || []).map((section, i) => (
                <div key={i} data-testid={`agreement-section-${i}`}>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-blue-900 mb-1">
                    {section.heading}
                  </div>
                  <p className="text-slate-700 leading-relaxed">{section.body}</p>
                </div>
              ))}
              <div className="pt-3 border-t border-slate-100">
                <div className="text-[11px] uppercase tracking-wider font-bold text-blue-900 mb-1">
                  YOUR RATES ON FILE
                </div>
                <div className="text-slate-700 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                  <span>In-person: ${(d.interpreter.hourly_rate || 0).toFixed(2)}/hr</span>
                  <span>OPI: ${(d.interpreter.opi_rate || 0).toFixed(2)}/{d.interpreter.opi_unit || 'hr'}</span>
                  <span>VRI: ${(d.interpreter.vri_rate || 0).toFixed(2)}/{d.interpreter.vri_unit || 'hr'}</span>
                  <span>Translation: ${(d.interpreter.translation_rate || 0).toFixed(4)}/word</span>
                </div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-3">
              Please read all sections above before signing. A signed PDF + DOCX copy will be emailed to you immediately upon signing.
            </div>
          </div>

          {/* Signature block */}
          <div className="p-6 border-t border-slate-100">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-3">
              Electronic signature
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs text-slate-700">Type your full legal name</Label>
                <Input
                  data-testid="sign-typed-name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder={d.interpreter.full_name || "Jane Q. Interpreter"}
                  className="mt-1.5"
                />
                <div className="text-[11px] text-slate-400 mt-1.5">
                  This typed name is a legal signature under the ESIGN Act and UETA.
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-xs text-slate-700">Draw your signature</Label>
                  <button
                    type="button"
                    onClick={clearCanvas}
                    data-testid="sign-clear-canvas"
                    className="text-[11px] text-slate-500 hover:text-slate-900 flex items-center gap-1"
                  >
                    <ArrowClockwise size={12} /> Clear
                  </button>
                </div>
                <div className="border-2 border-dashed border-slate-300 rounded-lg bg-white">
                  <canvas
                    ref={canvasRef}
                    data-testid="sign-canvas"
                    style={{ width: "100%", height: 140, touchAction: "none", cursor: "crosshair" }}
                  />
                </div>
                <div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                  <PencilSimple size={11} /> Use your mouse or finger to sign
                </div>
              </div>
            </div>

            <label className="flex items-start gap-2 mt-5 cursor-pointer">
              <Checkbox
                data-testid="sign-agree"
                checked={agree}
                onCheckedChange={(v) => setAgree(!!v)}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-700">
                I confirm that I am {d.interpreter.full_name || "the named contractor"}, I have read and agree to
                the Independent Contractor Agreement between me and {d.company.name}, and I intend my typed name
                and drawn signature above to be my legal electronic signature.
              </span>
            </label>

            <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                <ShieldCheck size={14} /> IP address and timestamp will be recorded for audit.
              </div>
              <Button
                data-testid="sign-submit"
                onClick={submit}
                disabled={submitting}
                className="bg-red-700 hover:bg-red-800 text-white"
              >
                {submitting ? "Signing…" : "Sign agreement"}
              </Button>
            </div>
          </div>
        </div>

        <div className="text-center text-[11px] text-slate-400 mt-6">
          © {new Date().getFullYear()} {d.company.name}. Electronic signature powered by Unity Language Group.
        </div>
        <PublicFooter />
      </div>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 shadow-sm">{children}</div>
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mb-2">{title}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-2 text-sm">
      <span className="text-xs text-slate-400 w-24 shrink-0">{label}</span>
      <span className="text-slate-900">{value || "—"}</span>
    </div>
  );
}
